import type { AuthDto } from '@mathflat/domain/@entities/Auth'
import type { MathflatApi } from '@mathflat/domain/@entities/Login/api'
import type { DefaultResponse } from '@mathflat/shared/@types/mathflatApi'
import type { AxiosRequestConfig } from 'axios'

import type { RestrictionType } from '../types/permission'
import maxios from '../utils/maxios'

type LoginInfoRes = {
  isPolicyAgreed: boolean
}

/** 학생앱의 restrictions로 오버라이드 */
export type StudentLoginResponseDto = Omit<AuthDto.LoginResponseDto, 'restrictions'> & {
  restrictions?: RestrictionType[]
}

export const authApi = {
  login: (payload: AuthDto.LoginRequestDto) => {
    return maxios.post<StudentLoginResponseDto>('/v2/login', payload)
  },

  // 네이버 웍스(웨일스페이스) SSO 로그인
  // b2g 외부 SSO 콜백(/whale/callback)에서 받은 code로 최종 로그인 처리
  postNaverWorksLogin: (payload: AuthDto.NaverWorksLoginRequestDto) => {
    return maxios.post<StudentLoginResponseDto>('/v2/login/oauth2/naver-works', payload)
  },

  // AIEP(광주교육청 통합 로그인) 브릿지 로그인
  // /aiep/bridge 진입 시 SAML 콜백 code로 JWT 로그인 응답을 받는다.
  postAiepLogin: (payload: AuthDto.AiepLoginRequestDto) => {
    return maxios.post<StudentLoginResponseDto>('/v2/login/saml2/aiep', payload)
  },

  logout: (data: AuthDto.LogoutRequestDto) => {
    return maxios.post('/v2/logout', data)
  },

  legacyLogout: ({ legacyToken }: AuthDto.LegacyLogoutRequestDto) => {
    return maxios.post(
      '/logout',
      {},
      {
        headers: {
          'x-auth-token': legacyToken,
        },
      },
    )
  },

  passwordChange: (payload: MathflatApi.Request.Password) => {
    return maxios.post('/v2/password', payload)
  },

  passwordForceChange: (
    payload: {
      newPassword: string
      newPasswordCheck: string
    },
    config?: AxiosRequestConfig,
  ) => {
    return maxios.patch('/v2/password/force-change', payload, config)
  },

  loginInfo: (payload: { phoneNumber: string; userType: 'STUDENT' | 'PARENT' }) => {
    return maxios.get<DefaultResponse<LoginInfoRes>>('/login/info', { params: payload })
  },

  // 선생님앱 iframe에서 학생 대리 로그인
  loginByStudentImpersonationAuthToken: (token: string) => {
    return maxios.post<StudentLoginResponseDto>(
      '/v2/login/with-student-impersonation-auth-token',
      { studentImpersonationAuthToken: token },
      { withoutAuth: true },
    )
  },
}
